import React, { useEffect, useState, useCallback } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Modal,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  CircularProgress,
  Paper,
  Grid,
  alpha,
  Snackbar,
  Alert,
  MenuItem,
  Chip,
  Pagination,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";
import {
  AddCircle as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CloudUpload as UploadIcon,
  CloudDownload as DownloadIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";

// const BASE = "https://api.medstown.com";
const BASE = "https://api.medstown.com";
const COLORS = {
  primary: "#014D4D",
  secondary: "#02A0A0",
  accent: "#FFB800",
  success: "#16a34a",
  error: "#dc2626",
  warning: "#d97706",
  muted: "#6b7280",
};

const StatCard = ({ label, value, color, loading }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 2,
      border: `1px solid ${alpha(color, 0.25)}`,
      backgroundColor: alpha(color, 0.06),
      minWidth: 140,
    }}
  >
    <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography variant="h5" sx={{ color, fontWeight: 700, mt: 0.5 }}>
      {loading ? "…" : value}
    </Typography>
  </Paper>
);

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "92%", sm: 560 },
  maxHeight: "92vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 3,
};

const emptyForm = {
  pincode: "",
  resolvedCity: null,
  resolvedDistrict: null,
  resolvedState: null,
  resolvedFormattedAddress: null,
  resolvedPlaceId: null,
  resolvedLat: null,
  resolvedLng: null,
  resolveError: null,
  resolving: false,
  instantDelivery: {
    enabled: false,
    cutoffHours: 2,
    slotWindowDays: 2,
    slotDurationMins: 60,
  },
  standardDelivery: {
    enabled: false,
    etaDaysMin: 2,
    etaDaysMax: 5,
  },
  isActive: true,
};

export default function ServiceableAreas() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [filterInstant, setFilterInstant] = useState("any");
  const [filterStandard, setFilterStandard] = useState("any");

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [failedPage, setFailedPage] = useState(1);
  const FAILED_PAGE_SIZE = 10;

  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await axios.get(`${BASE}/admin/serviceable-areas/stats`);
      setStats(res.data?.data || null);
    } catch {
      // silent
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize };
      if (search) params.search = search;
      if (filterInstant !== "any") params.instant = filterInstant;
      if (filterStandard !== "any") params.standard = filterStandard;
      const res = await axios.get(`${BASE}/admin/serviceable-areas`, {
        params,
      });
      const data = res.data?.data || {};
      setRows((data.list || []).map((r) => ({ ...r, id: r._id })));
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      toast.error("Failed to load Serviceable Pincodes");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, filterInstant, filterStandard]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row._id);
    setForm({
      pincode: row.pincode || "",
      resolvedCity: row.city || null,
      resolvedDistrict: row.district || null,
      resolvedState: row.state || null,
      resolvedFormattedAddress: row.formattedAddress || null,
      resolvedPlaceId: row.placeId || null,
      resolvedLat: row.latitude ?? null,
      resolvedLng: row.longitude ?? null,
      resolveError: null,
      resolving: false,
      instantDelivery: {
        ...emptyForm.instantDelivery,
        ...(row.instantDelivery || {}),
      },
      standardDelivery: {
        ...emptyForm.standardDelivery,
        ...(row.standardDelivery || {}),
      },
      isActive: row.isActive !== false,
    });
    setModalOpen(true);
  };

  // Debounced auto-resolve when typing a 6-digit pincode in create mode.
  useEffect(() => {
    if (!modalOpen || editingId) return;
    const pin = String(form.pincode || "").trim();
    if (!/^\d{6}$/.test(pin)) {
      if (form.resolvedCity || form.resolvedState || form.resolveError) {
        setForm((f) => ({
          ...f,
          resolvedCity: null,
          resolvedDistrict: null,
          resolvedState: null,
          resolvedFormattedAddress: null,
          resolvedPlaceId: null,
          resolvedLat: null,
          resolvedLng: null,
          resolveError: null,
          resolving: false,
        }));
      }
      return;
    }
    let cancelled = false;
    setForm((f) => ({ ...f, resolving: true, resolveError: null }));
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${BASE}/admin/serviceable-areas/lookup/${pin}`
        );
        if (cancelled) return;
        const d = res.data?.data || {};
        setForm((f) => ({
          ...f,
          resolvedCity: d.city || null,
          resolvedDistrict: d.district || null,
          resolvedState: d.state || null,
          resolvedFormattedAddress: d.formattedAddress || null,
          resolvedPlaceId: d.placeId || null,
          resolvedLat: d.latitude ?? null,
          resolvedLng: d.longitude ?? null,
          resolveError: null,
          resolving: false,
        }));
      } catch (err) {
        if (cancelled) return;
        setForm((f) => ({
          ...f,
          resolvedCity: null,
          resolvedDistrict: null,
          resolvedState: null,
          resolvedFormattedAddress: null,
          resolvedPlaceId: null,
          resolvedLat: null,
          resolvedLng: null,
          resolveError:
            err?.response?.data?.message ||
            "Could not verify this pincode via Google.",
          resolving: false,
        }));
      }
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [form.pincode, modalOpen, editingId]);

  const handleSave = async () => {
    if (!editingId) {
      if (!/^\d{6}$/.test(String(form.pincode || "").trim())) {
        setSnack({
          open: true,
          msg: "Pincode must be 6 digits.",
          severity: "warning",
        });
        return;
      }
      if (!form.resolvedCity || !form.resolvedState) {
        setSnack({
          open: true,
          msg: "Pincode not yet verified by Google. Wait or fix the pincode.",
          severity: "warning",
        });
        return;
      }
    }
    setSaving(true);
    try {
      const {
        resolvedCity,
        resolvedDistrict,
        resolvedState,
        resolvedFormattedAddress,
        resolvedPlaceId,
        resolvedLat,
        resolvedLng,
        resolveError,
        resolving,
        ...rest
      } = form;
      const payload = editingId ? rest : rest; // city/state set server-side from Google
      if (editingId) {
        await axios.put(
          `${BASE}/admin/serviceable-areas/${editingId}`,
          payload
        );
        toast.success("Updated");
      } else {
        await axios.post(`${BASE}/admin/serviceable-areas`, payload);
        toast.success("Created");
      }
      setModalOpen(false);
      fetchRows();
      fetchStats();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRow) return;
    try {
      await axios.delete(`${BASE}/admin/serviceable-areas/${deleteRow._id}`);
      toast.success("Deleted");
      setDeleteOpen(false);
      setDeleteRow(null);
      fetchRows();
      fetchStats();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleUpload = async () => {
    if (!csvFile) {
      setSnack({
        open: true,
        msg: "Please choose a CSV file.",
        severity: "warning",
      });
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      const res = await axios.post(
        `${BASE}/admin/serviceable-areas/upload-csv`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setUploadResult(res.data?.data || null);
      setFailedPage(1);
      toast.success("CSV processed");
      fetchRows();
      fetchStats();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSampleDownload = () => {
    window.open(`${BASE}/admin/serviceable-areas/sample-csv`, "_blank");
  };

  const handleDownloadFailed = () => {
    const failed = uploadResult?.failed || [];
    if (!failed.length) return;
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ["row", "pincode", "reason"];
    const csv = [
      header.join(","),
      ...failed.map((f) => [f.row, f.pincode, f.reason].map(escape).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `serviceable-areas-failed-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const columns = [
    {
      field: "pincode",
      headerName: "Pincode",
      width: 110,
      flex: 0.5,
      minWidth: 100,
    },
    // { field: "city", headerName: "City", flex: 1, minWidth: 120 },
    // {
    //   field: "district",
    //   headerName: "Area / District",
    //   flex: 1,
    //   minWidth: 130,
    // },
    // { field: "state", headerName: "State", flex: 1, minWidth: 120 },
    {
      field: "formattedAddress",
      headerName: "Place (Google)",
      flex: 1.4,
      minWidth: 220,
      sortable: false,
      renderCell: (p) => (
        <Tooltip title={p.row.formattedAddress || ""}>
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: COLORS.muted,
            }}
          >
            {p.row.formattedAddress || "—"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "instant",
      headerName: "Instant",
      width: 110,
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      renderCell: (p) =>
        p.row.instantDelivery?.enabled ? (
          <Chip
            label="ON"
            size="small"
            sx={{
              bgcolor: alpha(COLORS.success, 0.15),
              color: COLORS.success,
              fontWeight: 700,
            }}
          />
        ) : (
          <Chip
            label="OFF"
            size="small"
            sx={{
              bgcolor: alpha(COLORS.muted, 0.15),
              color: COLORS.muted,
              fontWeight: 700,
            }}
          />
        ),
    },
    {
      field: "standard",
      headerName: "Standard",
      width: 110,
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      renderCell: (p) =>
        p.row.standardDelivery?.enabled ? (
          <Chip
            label="ON"
            size="small"
            sx={{
              bgcolor: alpha(COLORS.secondary, 0.15),
              color: COLORS.secondary,
              fontWeight: 700,
            }}
          />
        ) : (
          <Chip
            label="OFF"
            size="small"
            sx={{
              bgcolor: alpha(COLORS.muted, 0.15),
              color: COLORS.muted,
              fontWeight: 700,
            }}
          />
        ),
    },
    {
      field: "eta",
      headerName: "Std ETA (days)",
      width: 130,
      flex: 0.6,
      minWidth: 110,
      sortable: false,
      valueGetter: (p) =>
        p.row.standardDelivery?.enabled
          ? `${p.row.standardDelivery.etaDaysMin}-${p.row.standardDelivery.etaDaysMax}`
          : "—",
    },
    {
      field: "isActive",
      headerName: "Active",
      width: 90,
      flex: 0.4,
      minWidth: 80,
      sortable: false,
      renderCell: (p) =>
        p.row.isActive ? (
          <Chip
            label="Yes"
            size="small"
            sx={{
              bgcolor: alpha(COLORS.success, 0.15),
              color: COLORS.success,
              fontWeight: 700,
            }}
          />
        ) : (
          <Chip
            label="No"
            size="small"
            sx={{
              bgcolor: alpha(COLORS.error, 0.15),
              color: COLORS.error,
              fontWeight: 700,
            }}
          />
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 110,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => openEdit(p.row)}>
              <EditIcon fontSize="small" sx={{ color: COLORS.primary }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => {
                setDeleteRow(p.row);
                setDeleteOpen(true);
              }}
            >
              <DeleteIcon fontSize="small" sx={{ color: COLORS.error }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ mt: 2 }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ color: COLORS.primary }}
        >
          Serviceable Pincodes
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            startIcon={<DownloadIcon />}
            onClick={handleSampleDownload}
            variant="outlined"
            size="small"
            sx={{ borderColor: COLORS.secondary, color: COLORS.secondary }}
          >
            Sample CSV
          </Button>
          <Button
            startIcon={<UploadIcon />}
            onClick={() => {
              setUploadResult(null);
              setCsvFile(null);
              setUploadOpen(true);
            }}
            variant="outlined"
            size="small"
            sx={{ borderColor: COLORS.primary, color: COLORS.primary }}
          >
            Upload CSV
          </Button>
          <Button
            startIcon={<AddIcon />}
            onClick={openCreate}
            variant="contained"
            size="small"
            sx={{ bgcolor: COLORS.primary, "&:hover": { bgcolor: "#013636" } }}
          >
            Add Pincode
          </Button>
        </Stack>
      </Stack>

      {/* Stats */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2}>
          <StatCard
            label="Total"
            value={stats?.total ?? 0}
            color={COLORS.primary}
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard
            label="Active"
            value={stats?.activeCount ?? 0}
            color={COLORS.success}
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard
            label="Instant ON"
            value={stats?.instantEnabled ?? 0}
            color={COLORS.accent}
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard
            label="Standard ON"
            value={stats?.standardEnabled ?? 0}
            color={COLORS.secondary}
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard
            label="Both"
            value={stats?.bothEnabled ?? 0}
            color={COLORS.success}
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard
            label="Neither"
            value={stats?.neitherEnabled ?? 0}
            color={COLORS.error}
            loading={statsLoading}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        sx={{ mb: 1.5 }}
      >
        <TextField
          size="small"
          placeholder="Search pincode / city / state"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <SearchIcon
                fontSize="small"
                sx={{ color: COLORS.muted, mr: 0.5 }}
              />
            ),
            endAdornment: search ? (
              <IconButton size="small" onClick={() => setSearch("")}>
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : null,
          }}
          sx={{ minWidth: 280 }}
        />
        <TextField
          select
          size="small"
          label="Instant"
          value={filterInstant}
          onChange={(e) => {
            setFilterInstant(e.target.value);
            setPage(1);
          }}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="any">All</MenuItem>
          <MenuItem value="true">Enabled</MenuItem>
          <MenuItem value="false">Disabled</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Standard"
          value={filterStandard}
          onChange={(e) => {
            setFilterStandard(e.target.value);
            setPage(1);
          }}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="any">All</MenuItem>
          <MenuItem value="true">Enabled</MenuItem>
          <MenuItem value="false">Disabled</MenuItem>
        </TextField>
      </Stack>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${alpha(COLORS.primary, 0.1)}`,
          borderRadius: 2,
        }}
      >
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={{ page: page - 1, pageSize }}
          onPaginationModelChange={(model) => {
            const nextPageSize = model.pageSize ?? pageSize;
            if (nextPageSize !== pageSize) {
              setPageSize(nextPageSize);
              setPage(1);
            } else {
              setPage((model.page ?? 0) + 1);
            }
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          disableRowSelectionOnClick
          sx={{ border: 0 }}
        />
      </Paper>

      {/* Create / Edit Modal */}
      <Modal open={modalOpen} onClose={() => !saving && setModalOpen(false)}>
        <Box sx={modalStyle}>
          <Typography
            variant="h6"
            sx={{ mb: 2, color: COLORS.primary, fontWeight: 700 }}
          >
            {editingId ? "Edit Serviceable Area" : "Add Serviceable Area"}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Pincode"
                value={form.pincode}
                onChange={(e) =>
                  setForm({
                    ...form,
                    pincode: e.target.value.replace(/\D/g, "").slice(0, 6),
                  })
                }
                disabled={!!editingId}
                fullWidth
                size="small"
                inputProps={{ maxLength: 6, inputMode: "numeric" }}
                error={!!form.resolveError}
                helperText={
                  form.resolving
                    ? "Verifying via Google…"
                    : form.resolveError
                    ? form.resolveError
                    : form.pincode && !/^\d{6}$/.test(form.pincode)
                    ? "Enter all 6 digits"
                    : " "
                }
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.2,
                  borderRadius: 2,
                  bgcolor: form.resolvedCity
                    ? alpha(COLORS.success, 0.06)
                    : alpha(COLORS.muted, 0.04),
                  borderColor: form.resolvedCity
                    ? alpha(COLORS.success, 0.3)
                    : alpha(COLORS.muted, 0.25),
                  minHeight: 56,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {form.resolving ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={14} />
                    <Typography variant="body2" sx={{ color: COLORS.muted }}>
                      Resolving from Google…
                    </Typography>
                  </Stack>
                ) : form.resolvedCity ? (
                  <Stack spacing={0.75} sx={{ width: "100%" }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: COLORS.muted, fontWeight: 600 }}
                      >
                        Verified
                      </Typography>
                      <Chip
                        label={`City: ${form.resolvedCity}`}
                        size="small"
                        sx={{
                          bgcolor: alpha(COLORS.success, 0.15),
                          color: COLORS.success,
                          fontWeight: 700,
                        }}
                      />
                      {form.resolvedDistrict && (
                        <Chip
                          label={`Area: ${form.resolvedDistrict}`}
                          size="small"
                          sx={{
                            bgcolor: alpha(COLORS.accent, 0.18),
                            color: "#8a5a00",
                            fontWeight: 700,
                          }}
                        />
                      )}
                      <Chip
                        label={`State: ${form.resolvedState}`}
                        size="small"
                        sx={{
                          bgcolor: alpha(COLORS.secondary, 0.15),
                          color: COLORS.secondary,
                          fontWeight: 700,
                        }}
                      />
                    </Stack>
                    {form.resolvedFormattedAddress && (
                      <Typography
                        variant="caption"
                        sx={{ color: COLORS.muted, display: "block" }}
                      >
                        📍 {form.resolvedFormattedAddress}
                      </Typography>
                    )}
                    {form.resolvedLat != null && form.resolvedLng != null && (
                      <Typography
                        variant="caption"
                        sx={{ color: COLORS.muted, fontFamily: "monospace" }}
                      >
                        {form.resolvedLat}, {form.resolvedLng}
                        {form.resolvedPlaceId
                          ? ` · ${form.resolvedPlaceId}`
                          : ""}
                      </Typography>
                    )}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: COLORS.muted }}>
                    {editingId
                      ? "City / state are auto-resolved from Google."
                      : "Enter a 6-digit pincode — city & state come from Google."}
                  </Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.instantDelivery.enabled}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          instantDelivery: {
                            ...form.instantDelivery,
                            enabled: e.target.checked,
                          },
                        })
                      }
                      color="success"
                    />
                  }
                  label={
                    <Typography fontWeight={700}>Instant Delivery</Typography>
                  }
                />
                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      label="Cutoff (hrs)"
                      type="number"
                      value={form.instantDelivery.cutoffHours}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          instantDelivery: {
                            ...form.instantDelivery,
                            cutoffHours: +e.target.value,
                          },
                        })
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      label="Window (days)"
                      type="number"
                      value={form.instantDelivery.slotWindowDays}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          instantDelivery: {
                            ...form.instantDelivery,
                            slotWindowDays: +e.target.value,
                          },
                        })
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      label="Slot (mins)"
                      type="number"
                      value={form.instantDelivery.slotDurationMins}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          instantDelivery: {
                            ...form.instantDelivery,
                            slotDurationMins: +e.target.value,
                          },
                        })
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      sx={{ color: COLORS.muted, display: "block", mt: 0.5 }}
                    >
                      Operating hours come from Admin Controller (e.g. "11 AM –
                      11 PM Order" or "Accept Orders 24hrs"). Toggle them there
                      to change the slot window.
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.standardDelivery.enabled}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          standardDelivery: {
                            ...form.standardDelivery,
                            enabled: e.target.checked,
                          },
                        })
                      }
                      color="primary"
                    />
                  }
                  label={
                    <Typography fontWeight={700}>Standard Delivery</Typography>
                  }
                />
                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                  <Grid item xs={6}>
                    <TextField
                      label="ETA min (days)"
                      type="number"
                      value={form.standardDelivery.etaDaysMin}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          standardDelivery: {
                            ...form.standardDelivery,
                            etaDaysMin: +e.target.value,
                          },
                        })
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="ETA max (days)"
                      type="number"
                      value={form.standardDelivery.etaDaysMax}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          standardDelivery: {
                            ...form.standardDelivery,
                            etaDaysMax: +e.target.value,
                          },
                        })
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) =>
                      setForm({ ...form, isActive: e.target.checked })
                    }
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>

          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <Button onClick={() => setModalOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={
                saving ||
                form.resolving ||
                (!editingId && (!form.resolvedCity || !form.resolvedState))
              }
              sx={{
                bgcolor: COLORS.primary,
                "&:hover": { bgcolor: "#013636" },
              }}
            >
              {saving ? (
                <CircularProgress size={18} sx={{ color: "white" }} />
              ) : (
                "Save"
              )}
            </Button>
          </Stack>
        </Box>
      </Modal>

      {/* Delete confirm */}
      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <Box sx={{ ...modalStyle, width: 380 }}>
          <Typography
            variant="h6"
            sx={{ mb: 1, color: COLORS.error, fontWeight: 700 }}
          >
            Delete pincode?
          </Typography>
          <Typography sx={{ mb: 2 }}>
            Pincode <b>{deleteRow?.pincode}</b> will be removed from serviceable
            list.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleDelete}>
              Delete
            </Button>
          </Stack>
        </Box>
      </Modal>

      {/* Upload CSV */}
      <Modal
        open={uploadOpen}
        onClose={() => !uploading && setUploadOpen(false)}
      >
        <Box sx={{ ...modalStyle, width: 480 }}>
          <Typography
            variant="h6"
            sx={{ mb: 1, color: COLORS.primary, fontWeight: 700 }}
          >
            Upload Serviceable Pincodes CSV
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.muted, mb: 2 }}>
            Existing pincodes will be updated; new ones will be added. City
            &amp; state are auto-resolved from Google for each pincode — no need
            to include them in the CSV.
          </Typography>
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadIcon />}
            sx={{ borderColor: COLORS.primary, color: COLORS.primary }}
          >
            {csvFile ? csvFile.name : "Choose CSV file"}
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
            />
          </Button>

          {uploadResult && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(COLORS.secondary, 0.06),
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                flexWrap="wrap"
                sx={{ mb: 0.5 }}
              >
                <Typography variant="body2">
                  Processed: <b>{uploadResult.processed}</b>
                </Typography>
                <Typography variant="body2">
                  Inserted: <b>{uploadResult.insertedCount}</b>
                </Typography>
                <Typography variant="body2">
                  Updated: <b>{uploadResult.modifiedCount}</b>
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.error }}>
                  Failed: <b>{uploadResult.failedCount}</b>
                </Typography>
              </Stack>

              {uploadResult.failed?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 0.5 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: COLORS.error, fontWeight: 700 }}
                    >
                      Failed pincodes ({uploadResult.failed.length})
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadFailed}
                      sx={{ color: COLORS.primary }}
                    >
                      Download report
                    </Button>
                  </Stack>

                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                      maxHeight: 260,
                      borderColor: alpha(COLORS.error, 0.25),
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, width: 60 }}>
                            Row
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 100 }}>
                            Pincode
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {uploadResult.failed
                          .slice(
                            (failedPage - 1) * FAILED_PAGE_SIZE,
                            failedPage * FAILED_PAGE_SIZE
                          )
                          .map((f, i) => (
                            <TableRow key={`${f.row}-${i}`}>
                              <TableCell>{f.row}</TableCell>
                              <TableCell sx={{ fontFamily: "monospace" }}>
                                {f.pincode}
                              </TableCell>
                              <TableCell sx={{ color: COLORS.error }}>
                                {f.reason}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {uploadResult.failed.length > FAILED_PAGE_SIZE && (
                    <Stack
                      direction="row"
                      justifyContent="center"
                      sx={{ mt: 1 }}
                    >
                      <Pagination
                        size="small"
                        count={Math.ceil(
                          uploadResult.failed.length / FAILED_PAGE_SIZE
                        )}
                        page={failedPage}
                        onChange={(_, p) => setFailedPage(p)}
                        color="primary"
                      />
                    </Stack>
                  )}
                </Box>
              )}
            </Box>
          )}

          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <Button onClick={() => setUploadOpen(false)} disabled={uploading}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={uploading || !csvFile}
              sx={{
                bgcolor: COLORS.primary,
                "&:hover": { bgcolor: "#013636" },
              }}
            >
              {uploading ? (
                <CircularProgress size={18} sx={{ color: "white" }} />
              ) : (
                "Upload"
              )}
            </Button>
          </Stack>
        </Box>
      </Modal>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snack.severity} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
